<template>
  <div class="company-orders">
    <TransactionsTitleRow
      :waitingCount="waitingOrdersCount"
      :inProgressCount="inProgressOrdersCount"
      :showBatchActions="filteredWaitingOrdersCount > 0"
      :isAdmin="props.is_admin"
      :isBatchProcessing="companyOrderStore.isBatchProcessing"
      :exportDisabled="filteredOrders.length === 0"
      @toggle:waiting="toggleStatusFilter([OrderStatus.WAITING_CONFIRMATION])"
      @toggle:in-progress="toggleStatusFilter(IN_PROGRESS_STATUSES)"
      @confirm:all="showConfirmAllPrompt = true"
      @reject:all="props.is_admin ? rejectAllOrders() : cancelAllOrders()"
      @export="exportOrdersCsv(filteredOrders, isConsolidatedView, t)"
    />

    <LoadingIndicatorComponent
      v-if="isInitialLoading || companyOrderStore.isLoading"
    />

    <TransactionsTable
      v-else
      v-model:filters="filters"
      v-model:sortOrder="sortOrder"
      v-model:dateRange="dateRange"
      :rows="rows"
      :loadedRowCount="loadedRowCount"
      :isAdmin="props.is_admin"
      :showCompany="isConsolidatedView"
      :hasLinkedEvents="transactionLogStore.hasLinkedEvents"
      :portfolioOptions="portfolioOptions"
      :canLoadMore="canLoadMoreEvents"
      :isLoadingMore="transactionLogStore.isLoadingMore"
      @view:order="companyOrderStore.setSelectedOrder"
      @view:event="selectedEvent = $event"
      @view:group="selectedGroup = $event"
      @confirm:order="promptConfirmOrder"
      @reject:order="rejectOrder"
      @admin:status="adminUpdateStatus"
      @admin:retry="adminRetryOrder"
      @manage:company="manageAndNavigate"
      @reset:date-range="dateRange = null"
      @load:more="loadMoreEvents"
    />

    <CompanyOrderDetailsModal
      v-if="companyOrderStore.selectedOrder"
      :order="companyOrderStore.selectedOrder"
      :is_admin="props.is_admin"
      @close="companyOrderStore.setSelectedOrder(null)"
      @order:confirmed="handleOrderConfirmed"
    />

    <RegistryEventModal
      v-if="selectedEvent"
      :event="selectedEvent"
      @close="selectedEvent = null"
    />

    <RegistryEventGroupModal
      v-if="selectedGroup"
      :group="selectedGroup"
      @close="selectedGroup = null"
    />

    <ConfirmationModalComponent
      :visible="!!orderPendingConfirmation"
      :title="$t('company_orders.confirm_order')"
      :message="$t('company_orders.confirm_order_message')"
      confirm-button-class="btn-success"
      @confirm="executeConfirmOrder"
      @cancel="cancelConfirmOrderPrompt"
    >
      <CancelConfirmationSummary
        v-if="pendingConfirmationSummaries.length > 0"
        :summaries="pendingConfirmationSummaries"
      />
    </ConfirmationModalComponent>

    <ConfirmationModalComponent
      :visible="showConfirmAllPrompt"
      :title="$t('company_orders.confirm_all')"
      :message="
        $t('company_orders.confirm_all_message', {
          count: filteredWaitingOrdersCount,
        })
      "
      confirm-button-class="btn-success"
      @confirm="executeConfirmAllOrders"
      @cancel="showConfirmAllPrompt = false"
    />
  </div>
</template>

<script setup lang="ts">
import { manageAccount } from "@/admin/manageAccount";
import ConfirmationModalComponent from "@/components/common/ConfirmationModalComponent.vue";
import LoadingIndicatorComponent from "@/components/common/LoadingIndicatorComponent.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import type { Company } from "@/types";
import { addDays, startOfDay } from "date-fns";
import { isEqual, uniq } from "lodash-es";
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import type { CompanyOrder } from "../common/companyOrderTypes";
import { OrderStatus } from "../common/companyOrderTypes";
import { useCompanyOrderStore } from "../common/useCompanyOrderStore";
import CancelConfirmationSummary from "./CancelConfirmationSummary.vue";
import CompanyOrderDetailsModal from "./CompanyOrderDetailsModal.vue";
import { extractCancellationSummaries } from "./confirmCancellationSummary";
import type { CancellationSummary } from "./confirmCancellationSummary";
import { exportOrdersCsv } from "./ordersCsv";
import RegistryEventGroupModal from "./RegistryEventGroupModal.vue";
import RegistryEventModal from "./RegistryEventModal.vue";
import {
  areEventRowsVisible,
  emptyTransactionFilters,
  IN_PROGRESS_STATUSES,
  selectedEventTypes,
} from "./transactionFilters";
import type {
  RegistryEventSummary,
  RegistryEventsQuery,
  SortOrder,
} from "./transactionLogTypes";
import {
  buildTransactionRows,
  filterOrders,
  type EventGroupRow,
} from "./transactionRows";
import TransactionsTable from "./TransactionsTable.vue";
import TransactionsTitleRow from "./TransactionsTitleRow.vue";
import { useTransactionLogStore } from "./useTransactionLogStore";

/**
 * The Transactions tab: the companies' orders merged with the registry events no order explains.
 * With `loadNonCompletedOnly` it serves the admin view of every company's open orders instead;
 * that view passes no companies, so it loads no registry events.
 */
const props = withDefaults(
  defineProps<{
    companies?: Company[];
    is_admin?: boolean;
    loadNonCompletedOnly?: boolean;
  }>(),
  {
    companies: () => [],
    is_admin: false,
    loadNonCompletedOnly: false,
  }
);

const { t } = useI18n();
const router = useRouter();
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const companyOrderStore = useCompanyOrderStore();
const transactionLogStore = useTransactionLogStore();

const filters = ref(emptyTransactionFilters());
const sortOrder = ref<SortOrder>("desc");
const dateRange = ref<Date[] | null>(null);
const isInitialLoading = ref(true);
const selectedEvent = ref<RegistryEventSummary | null>(null);
const selectedGroup = ref<EventGroupRow | null>(null);
const orderPendingConfirmation = ref<CompanyOrder | null>(null);
const pendingConfirmationSummaries = ref<CancellationSummary[]>([]);
const showConfirmAllPrompt = ref(false);

const isConsolidatedView = computed(
  () => props.companies.length > 1 || props.loadNonCompletedOnly
);

const companyIds = computed(() =>
  props.companies
    .map((company) => company.id)
    .filter((id): id is number => id !== undefined)
);

const dateBounds = computed(() => {
  const [firstDay, lastDay] = dateRange.value ?? [];
  return {
    from: firstDay ? startOfDay(firstDay).toISOString() : null,
    before: lastDay ? addDays(startOfDay(lastDay), 1).toISOString() : null,
  };
});

const loadedRowCount = computed(
  () => companyOrderStore.orders.length + transactionLogStore.events.length
);

const rows = computed(() =>
  buildTransactionRows({
    orders: companyOrderStore.orders,
    events: transactionLogStore.events,
    filters: filters.value,
    sortOrder: sortOrder.value,
    allEventsLoaded: transactionLogStore.nextCursor === null,
    t,
  })
);

const filteredOrders = computed(() =>
  filterOrders(companyOrderStore.orders, filters.value)
);

const filteredWaitingOrdersCount = computed(
  () =>
    filteredOrders.value.filter(
      (order) => order.status === OrderStatus.WAITING_CONFIRMATION
    ).length
);

const ordersIgnoringStatusFilter = computed(() =>
  filterOrders(companyOrderStore.orders, { ...filters.value, statuses: [] })
);

const waitingOrdersCount = computed(
  () =>
    ordersIgnoringStatusFilter.value.filter(
      (order) => order.status === OrderStatus.WAITING_CONFIRMATION
    ).length
);

const inProgressOrdersCount = computed(
  () =>
    ordersIgnoringStatusFilter.value.filter((order) =>
      IN_PROGRESS_STATUSES.includes(order.status)
    ).length
);

const portfolioOptions = computed(() =>
  uniq([
    ...companyOrderStore.orders
      .map((order) => order.source_portfolio_name)
      .filter((name): name is string => !!name),
    ...transactionLogStore.events.flatMap(
      (event) => event.allocation?.portfolio_names ?? []
    ),
  ]).sort()
);

const eventsQuery = computed<RegistryEventsQuery>(() => ({
  company_ids: companyIds.value,
  date_from: dateBounds.value.from,
  date_before: dateBounds.value.before,
  event_types: selectedEventTypes(filters.value),
  sort_order: sortOrder.value,
}));

const canLoadMoreEvents = computed(
  () => transactionLogStore.canLoadMore && areEventRowsVisible(filters.value)
);

onMounted(async () => {
  transactionLogStore.reset();
  await Promise.all([loadOrders(), loadEvents()]);
  isInitialLoading.value = false;
});

watch([companyIds, dateBounds], (scope, previous) => {
  if (!isEqual(scope, previous)) loadOrders();
});

watch(eventsQuery, (query, previous) => {
  if (!isEqual(query, previous)) loadEvents();
});

const loadOrders = () => {
  const { from, before } = dateBounds.value;
  return runReportingErrors(() =>
    props.loadNonCompletedOnly
      ? companyOrderStore.fetchAdminNonCompletedOrders(from, before)
      : companyOrderStore.fetchOrders(companyIds.value, from, before)
  );
};

const loadEvents = () =>
  companyIds.value.length === 0
    ? Promise.resolve()
    : runReportingErrors(() =>
        transactionLogStore.fetchFirstPage(eventsQuery.value)
      );

const loadMoreEvents = () =>
  runReportingErrors(transactionLogStore.fetchNextPage);

const runReportingErrors = async (action: () => Promise<unknown>) => {
  try {
    await action();
  } catch (error) {
    globalErrorHandler(error);
  }
};

const toggleStatusFilter = (statuses: OrderStatus[]) => {
  const isApplied = isEqual(
    [...filters.value.statuses].sort(),
    [...statuses].sort()
  );
  filters.value = {
    ...filters.value,
    statuses: isApplied ? [] : [...statuses],
  };
};

const manageAndNavigate = async (order: CompanyOrder) => {
  try {
    await manageAccount(order.account_id);
    const route = router.resolve({
      name: "Dashboard",
      query: {
        company_id: order.company_id,
        consolidated: "false",
        "certificates-history-active-tab": "orders",
      },
    });
    window.open(route.href, "_blank", "noopener,noreferrer");
  } catch (error) {
    globalErrorHandler(error);
  }
};

const promptConfirmOrder = (order: CompanyOrder) => {
  orderPendingConfirmation.value = order;
  // NOTE: returns [] for non-cancel types and malformed order_details, so the prompt falls back to
  // the plain confirm text there.
  pendingConfirmationSummaries.value = extractCancellationSummaries(
    order.order_type,
    order.order_details
  );
};

const cancelConfirmOrderPrompt = () => {
  orderPendingConfirmation.value = null;
  pendingConfirmationSummaries.value = [];
};

const executeConfirmOrder = async () => {
  const order = orderPendingConfirmation.value;
  cancelConfirmOrderPrompt();
  if (!order) return;
  await runReportingErrors(() => companyOrderStore.confirmOrder(order.id));
};

const rejectOrder = (order: CompanyOrder) =>
  runReportingErrors(() =>
    props.is_admin
      ? companyOrderStore.adminUpdateStatus(order.id, "CANCELLED")
      : companyOrderStore.cancelOrder(order.id)
  );

const handleOrderConfirmed = (orderId: number) => {
  companyOrderStore.updateOrderStatus(orderId, OrderStatus.PENDING);
};

const adminRetryOrder = (order: CompanyOrder) =>
  runReportingErrors(() => companyOrderStore.adminRetryOrder(order.id));

const adminUpdateStatus = (
  order: CompanyOrder,
  status: "COMPLETED" | "CANCELLED"
) =>
  runReportingErrors(() =>
    companyOrderStore.adminUpdateStatus(order.id, status)
  );

const executeConfirmAllOrders = async () => {
  showConfirmAllPrompt.value = false;
  try {
    const result = await companyOrderStore.confirmAllWaitingOrders(
      filteredOrders.value
    );
    reportBatchResult(
      result,
      "company_orders.batch_confirm_success",
      "company_orders.batch_confirm_partial"
    );
  } catch (error) {
    globalErrorHandler(error);
  }
};

const cancelAllOrders = async () => {
  try {
    const result = await companyOrderStore.cancelAllWaitingOrders(
      filteredOrders.value
    );
    reportBatchResult(
      result,
      "company_orders.batch_reject_success",
      "company_orders.batch_reject_partial"
    );
  } catch (error) {
    globalErrorHandler(error);
  }
};

const rejectAllOrders = async () => {
  try {
    const result = await companyOrderStore.rejectAllWaitingOrders(
      filteredOrders.value
    );
    reportBatchResult(
      result,
      "company_orders.batch_reject_success",
      "company_orders.batch_reject_partial"
    );
  } catch (error) {
    globalErrorHandler(error);
  }
};

const reportBatchResult = (
  result: { success: number; failed: number },
  successKey: string,
  partialKey: string
) => {
  if (result.success === 0) return;
  const message =
    result.failed > 0
      ? t(partialKey, { success: result.success, failed: result.failed })
      : t(successKey, { count: result.success });
  globalSuccessHandler(t("common.success"), message);
};
</script>

<style scoped lang="scss">
.company-orders {
  .badge {
    font-size: 0.75rem;
  }
}
</style>
